import { useState } from 'react';
import { db } from '../../firebase/config.js';
import { useFirestore } from '../../firebase/useFirestore';
import { parseHours } from '../../helpers/msiMonetisationHelpers/resolveInterventionDose';
import { invalidateInterventionDoseCache } from '../../hooks/useInterventionDoseContext';

/** @typedef {'Months'|'HoursPerWeek'} DoseField */

/** @type {Array<{key: DoseField, label: string}>} */
const FIELDS = [
    { key: 'Months', label: 'Duur in maanden' },
    { key: 'HoursPerWeek', label: 'Uren per week' },
];

/**
 * Instellingen → Activiteiten: de standaard duur en uren per week van de
 * deelname (`CompagnyMeta.DefaultInterventionDose`). De rekenregels van de
 * meetstandaard gebruiken die voor de tijdshorizon als een deelnemer zelf
 * geen startdatum of uren heeft, en voor anonieme respondenten.
 *
 * Opslaan bij het verlaten van het veld; leeg is 'niet ingesteld', niet 0.
 * AI-CONTEXT: See docs/features/msi-rekenregels-monetarisering.md (besluit R1, R5)
 *
 * @returns {JSX.Element}
 */
const InterventionDoseSettings = () => {
    const compagny = useFirestore('CompagnyMeta');
    const [messages, setMessages] = useState(/** @type {Record<string, string>} */ ({}));

    const comp = Array.isArray(compagny) ? compagny[0] : undefined;
    const dose = comp?.DefaultInterventionDose || {};

    /** @param {DoseField} key @param {string} message */
    const setMessage = (key, message) => setMessages((prev) => ({ ...prev, [key]: message }));

    /**
     * @param {DoseField} key
     * @param {string} raw - Wat er in het veld staat
     */
    const blurHandler = (key, raw) => {
        const value = raw.trim() === '' ? null : parseHours(raw);
        if (raw.trim() !== '' && value === null) {
            setMessage(key, 'Vul een getal in van 0 of hoger.');
            return;
        }
        setMessage(key, '');
        if (value === (parseHours(dose[key]) ?? null)) return;
        db.collection('CompagnyMeta')
            .doc(comp.docid)
            .update({ [`DefaultInterventionDose.${key}`]: value })
            .then(invalidateInterventionDoseCache)
            .catch(() => setMessage(key, 'Opslaan is niet gelukt. Probeer het opnieuw.'));
    };

    return (
        // id: doel van de link 'Naar de standaard' op de pagina Impact per euro (koppelingAdvies).
        <div className="dashboard-container" id="settings-duur-intensiteit">
            <h2>Duur en intensiteit van de deelname</h2>
            <p>Geldt als een deelnemer geen startdatum of uren per week heeft, en voor anonieme respondenten.</p>
            {!comp ? (
                <p className="status-config-loading">Instellingen worden geladen…</p>
            ) : (
                FIELDS.map(({ key, label }) => (
                    <div key={key} className="settings-general-module-settings-item-container">
                        <p>{label}</p>
                        <div>
                            {/* Ongestuurd, met de opgeslagen waarde in de key: na het
                                opslaan haakt het veld opnieuw aan, bij een fout blijft
                                de getypte tekst staan. */}
                            <input
                                key={`${key}:${dose[key] ?? ''}`}
                                type="text"
                                inputMode="decimal"
                                defaultValue={dose[key] ?? ''}
                                onBlur={(e) => blurHandler(key, e.target.value)}
                                aria-label={label}
                            />
                            {messages[key] && <p className="fixed-data-message" role="alert">{messages[key]}</p>}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

export default InterventionDoseSettings;
